/**
 * usePrimitiveRelationshipExplorer — Generic relationship discovery for any primitive.
 *
 * Given a primitive type and instance ID, queries the backend for all 1-hop
 * connected entities and transforms the result into UniversalConstellationData
 * for rendering in UniversalConstellationGraph.
 *
 * Double-click any node → onRecenter fires → re-query with new center.
 */

import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import type {
  UniversalConstellationData,
  ConstellationNode,
  ConstellationEdge,
  ConstellationRole,
} from './useUniversalConstellation';

// ---------------------------------------------------------------------------
// GraphQL Query
// ---------------------------------------------------------------------------

const PRIMITIVE_RELATIONSHIPS_QUERY = gql`
  query PrimitiveRelationships(
    $primitiveType: String!
    $primitiveId: String!
    $maxPerType: Int
  ) {
    primitiveRelationships(
      primitiveType: $primitiveType
      primitiveId: $primitiveId
      maxPerType: $maxPerType
    ) {
      center {
        nodeId
        label
        primitiveType
        role
        ring
        childCount
        status
        phase
        metadata {
          key
          value
        }
      }
      nodes {
        nodeId
        label
        primitiveType
        role
        ring
        childCount
        status
        phase
        metadata {
          key
          value
        }
      }
      edges {
        sourceId
        targetId
        edgeType
        label
        direction
      }
      relatedCounts {
        primitiveType
        count
        edgeLabel
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RelationshipNodeGql {
  nodeId: string;
  label: string;
  primitiveType: string;
  role: 'CENTER' | 'RELATED';
  ring: number;
  childCount: number;
  status: string | null;
  phase: 'DESIGN' | 'CONFIGURE' | 'EXECUTE' | 'MONITOR' | 'OPTIMIZE';
  metadata: Array<{ key: string; value: string }>;
}

interface RelationshipEdgeGql {
  sourceId: string;
  targetId: string;
  edgeType: string;
  label: string;
  direction: 'OUTGOING' | 'INCOMING' | 'REFERENCE';
}

interface RelatedCountGql {
  primitiveType: string;
  count: number;
  edgeLabel: string;
}

interface PrimitiveRelationshipsResult {
  primitiveRelationships: {
    center: RelationshipNodeGql;
    nodes: RelationshipNodeGql[];
    edges: RelationshipEdgeGql[];
    relatedCounts: RelatedCountGql[];
  };
}

// ---------------------------------------------------------------------------
// Phase → Color mapping (for node styling in UniversalConstellationGraph)
// ---------------------------------------------------------------------------

const PHASE_COLORS: Record<string, string> = {
  DESIGN: '#7048e8',
  CONFIGURE: '#1c7ed6',
  EXECUTE: '#2f9e44',
  MONITOR: '#e8590c',
  OPTIMIZE: '#0ca678',
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UsePrimitiveRelationshipExplorerProps {
  primitiveType: string;
  primitiveId: string;
  maxPerType?: number;
  skip?: boolean;
}

export interface UsePrimitiveRelationshipExplorerReturn {
  constellation: UniversalConstellationData | null;
  relatedCounts: RelatedCountGql[];
  loading: boolean;
  error: Error | undefined;
}

export function usePrimitiveRelationshipExplorer(
  props: UsePrimitiveRelationshipExplorerProps,
): UsePrimitiveRelationshipExplorerReturn {
  const { primitiveType, primitiveId, maxPerType = 20, skip = false } = props;

  const { data, loading, error } = useQuery<PrimitiveRelationshipsResult>(
    PRIMITIVE_RELATIONSHIPS_QUERY,
    {
      variables: { primitiveType, primitiveId, maxPerType },
      fetchPolicy: 'cache-and-network',
      skip: skip || !primitiveType || !primitiveId,
    },
  );

  const constellation = useMemo((): UniversalConstellationData | null => {
    if (!data?.primitiveRelationships) return null;

    const result = data.primitiveRelationships;

    // Transform backend nodes → ConstellationNode[]
    const nodes: ConstellationNode[] = result.nodes.map((n) => ({
      nodeId: n.nodeId,
      name: n.label,
      role: mapRole(n.role, n.primitiveType),
      metadata: {
        ...Object.fromEntries(n.metadata.map((m) => [m.key, m.value])),
        primitiveType: n.primitiveType,
        phase: n.phase,
        phaseColor: PHASE_COLORS[n.phase] ?? '#868e96',
        childCount: String(n.childCount),
        ...(n.status ? { status: n.status } : {}),
      },
    }));

    // Transform backend edges → ConstellationEdge[]
    const edges: ConstellationEdge[] = result.edges.map((e) => ({
      sourceId: e.sourceId,
      targetId: e.targetId,
      edgeType: e.edgeType,
      label: e.label,
    }));

    // Center node
    const center: ConstellationNode = {
      nodeId: result.center.nodeId,
      name: result.center.label,
      role: 'center',
      metadata: {
        primitiveType: result.center.primitiveType,
        phase: result.center.phase,
        phaseColor: PHASE_COLORS[result.center.phase] ?? '#868e96',
        ...Object.fromEntries(result.center.metadata.map((m) => [m.key, m.value])),
      },
    };

    // Count organization-role and regulatory-role nodes
    const orgCount = nodes.filter((n) => n.role === 'organization').length;
    const regCount = nodes.filter((n) => n.role === 'regulatory').length;

    return {
      center,
      nodes,
      edges,
      organizationCount: orgCount,
      regulatoryCount: regCount,
      uniqueRegulatoryCount: regCount,
      sharedRegulatoryCount: 0,
    };
  }, [data]);

  const relatedCounts = data?.primitiveRelationships?.relatedCounts ?? [];

  return { constellation, relatedCounts, loading, error };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Map backend role to ConstellationRole.
 * We use 'organization' for all ring-1 nodes so UniversalConstellationGraph
 * places them on ring 1 (concentric value 80).
 */
function mapRole(
  role: 'CENTER' | 'RELATED',
  _primitiveType: string,
): ConstellationRole {
  if (role === 'CENTER') return 'center';
  return 'organization';
}
